//src/app/features/services/SubscribedServicesCard/index.tsx
import { IOrder, ISubscribedServicesItems } from 'app/services/responseTypes';
import React from 'react';
import { capitalize } from 'lodash';
import { useAuth } from 'utils/AuthProvider';
import { CancelIcon } from 'assets/icons';
import { useCancelServiceMutation } from 'app/services/payments/orders';
import useConfirmContinue from 'app/hooks/useConfirmContinue';
import { successToast } from 'Components/Notifications';
import { USDate } from 'Components/Dates/USDate';
import Button from 'Components/Buttons/Button';
import UserBox from 'Components/UserBox';
import { DashboardContainerHeader } from 'app/layout/dashboardLayout/BoxLayout';

export interface IServicesItemsProps {
  service: ISubscribedServicesItems;
  onDelete: () => void;
}

const SubscribedServicesCard = ({ service, onDelete }: IServicesItemsProps) => {
  const { isAdmin } = useAuth();
  const [cancelService] = useCancelServiceMutation();
  const confirmContinue = useConfirmContinue();

  return (
    <div className="w-2/5 md-max:w-full border border-white rounded-3xl shadow-all">
      <DashboardContainerHeader
        title={
          <UserBox
            user={service.user}
            theme="white"
            px=""
            py=""
            shadow=""
            border=""
            profileLink={`/dashboard/clients/${service.user.id}`}
            content={
              service.details?.business?.name !== 'personal_business' &&
              capitalize(service.details?.business?.name).replace('_', ' ')
            }
          />
        }
      >
        <div
          className={`text-xs font-bold ${
            service.state === 'paid' && 'text-green'
          } ${service.state === 'cancelled' && 'text-gray-700'} ${
            service.state === 'pending_payment' && 'text-red-dark'
          }`}
        >
          {capitalize(service.state).replace('_', ' ')}
        </div>
      </DashboardContainerHeader>

      <div className="text-sm grid w-full gap-2 text-gray-700 p-4">
        <div>
          <span className="capitalize flex gap-4 items-center">
            <span className="text-gray-dark">{service.product_meta.title}</span>
            <span className="ml-auto shadow rounded-md px-2 py-1">
              {service.price_meta.type}
            </span>
            <span className="text-green text-regular font-bold inline-flex items-center">
              ${service.price_meta.amount_cents / 100}
            </span>
          </span>
          <span className="ml-2">{service.product_meta.description}</span>
          <div className="whitespace-pre-wrap ml-2">
            {service.product_meta?.features?.map((f) => `- ${f}`).join('\n')}
          </div>
        </div>

        {isAdmin && service.state === 'paid' && (
          <div className="flex text-xs gap-2">
            <div className="text-gray-dark">Stripe Payment Code:</div>
            <div className="select-all text-gray-700">
              {service.payment_intent_id || 'N/A'}
            </div>
          </div>
        )}

        <div className="flex w-full items-center text-xs gap-2">
          <div className="text-gray-dark">Subscribed at</div>
          <div className="select-all text-gray-700">
            <USDate date={service.created_at} />
          </div>

          {isAdmin && service.state === 'paid' && (
            <Button
              theme="red-outline"
              className="ml-auto max-w-min"
              onClick={() =>
                confirmContinue({
                  confirmText: 'Yes',
                  title: `Cancelation`,
                  text: 'Are you sure you want to cancel?',
                  cancelText: 'No',
                  theme: 'danger',
                  onConfirm: () =>
                    cancelService(service.id)
                      .unwrap()
                      .then(() => {
                        onDelete && onDelete();
                        successToast(`Service has been successfully cancelled`);
                      }),
                })
              }
            >
              <CancelIcon /> Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscribedServicesCard;
